import { Drawer, Typography, Space, Tag, Statistic, List, Button, Popconfirm, message, Divider } from 'antd';
import { StopOutlined, UndoOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { ContentReportResponse } from '@wisecure/api-client';
import type { ModerationContentGroup } from '@/types/dashboard';
import { ContentPreview } from './ContentPreview';
import { queryKeys } from '@/api/query-keys';
import { error as errorColors, warning, success, info, neutral } from '@/theme';

const { Text } = Typography;

const REASON_LABELS: Record<string, string> = {
  SPAM: 'Spam',
  HARASSMENT: 'Harcèlement',
  INAPPROPRIATE_CONTENT: 'Contenu inapproprié',
  MISINFORMATION: 'Désinformation',
  VIOLENCE: 'Violence',
  HATE_SPEECH: 'Discours haineux',
  OTHER: 'Autre',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'En attente',
  REVIEWED: 'Examiné',
  RESOLVED: 'Résolu',
  DISMISSED: 'Rejeté',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: warning[400],
  REVIEWED: info[400],
  RESOLVED: success[400],
  DISMISSED: neutral[300],
};

interface ReportDetailDrawerProps {
  group: ModerationContentGroup | null;
  open: boolean;
  onClose: () => void;
}

export function ReportDetailDrawer({ group, open, onClose }: ReportDetailDrawerProps) {
  const invalidate = useInvalidateQueries();

  const invalidateAll = () => {
    invalidate(queryKeys.contentReports());
    invalidate(queryKeys.contentReportsPendingCount);
  };

  const hideMutation = useWisecureMutation(
    (client, id: string) => client.contentReports.contentReportsControllerHide({ id }),
    {
      onSuccess: () => {
        message.success('Contenu masqué avec succès.');
        invalidateAll();
        onClose();
      },
      onError: () => message.error('Erreur lors du masquage du contenu.'),
    },
  );

  const restoreMutation = useWisecureMutation(
    (client, id: string) => client.contentReports.contentReportsControllerRestore({ id }),
    {
      onSuccess: () => {
        message.success('Contenu restauré avec succès.');
        invalidateAll();
        onClose();
      },
      onError: () => message.error('Erreur lors de la restauration du contenu.'),
    },
  );

  const dismissMutation = useWisecureMutation(
    (client, id: string) =>
      client.contentReports.contentReportsControllerResolve({
        id,
        requestBody: { status: 'DISMISSED' },
      }),
    {
      onSuccess: () => {
        message.success('Signalement rejeté.');
        invalidateAll();
      },
      onError: () => message.error('Erreur lors du rejet du signalement.'),
    },
  );

  if (!group) return null;

  const anyReportId = group.reporters[0]?.id;

  return (
    <Drawer
      title="Examen du contenu signalé"
      open={open}
      onClose={onClose}
      width={520}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {group.moderationStatus === 'VISIBLE' ? (
            <Popconfirm
              title="Masquer ce contenu ?"
              description="Tous les signalements en attente seront résolus."
              onConfirm={() => anyReportId && hideMutation.mutate(anyReportId)}
              okText="Masquer"
              cancelText="Annuler"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<StopOutlined />}
                loading={hideMutation.isPending}
              >
                Masquer le contenu
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Restaurer ce contenu ?"
              description="Le contenu redeviendra visible."
              onConfirm={() => anyReportId && restoreMutation.mutate(anyReportId)}
              okText="Restaurer"
              cancelText="Annuler"
            >
              <Button
                type="primary"
                icon={<UndoOutlined />}
                loading={restoreMutation.isPending}
              >
                Restaurer le contenu
              </Button>
            </Popconfirm>
          )}
        </div>
      }
    >
      <ContentPreview
        preview={group.contentPreview}
        moderationStatus={group.moderationStatus}
      />

      <Divider />

      <Space size="large" style={{ marginBottom: 16 }}>
        <Statistic
          title="Signalements"
          value={group.reportCount}
          valueStyle={{ color: group.reportCount >= 5 ? errorColors[400] : undefined, fontWeight: 700 }}
        />
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Statut contenu</Text>
          <Tag
            style={{
              backgroundColor: `${group.moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500]}14`,
              color: group.moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500],
              borderColor: `${group.moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500]}40`,
              borderRadius: 100,
              marginTop: 4,
            }}
          >
            {group.moderationStatus === 'HIDDEN' ? 'Masqué' : 'Visible'}
          </Tag>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Signalements statut</Text>
          <Tag
            style={{
              backgroundColor: `${STATUS_COLORS[group.worstStatus] ?? neutral[300]}14`,
              color: STATUS_COLORS[group.worstStatus] ?? neutral[500],
              borderColor: `${STATUS_COLORS[group.worstStatus] ?? neutral[300]}40`,
              borderRadius: 100,
              marginTop: 4,
            }}
          >
            {STATUS_LABELS[group.worstStatus] ?? group.worstStatus}
          </Tag>
        </div>
      </Space>

      <Divider style={{ margin: '16px 0' }} />

      <Text strong style={{ display: 'block', marginBottom: 12 }}>
        Signalements ({group.reporters.length})
      </Text>

      <List
        size="small"
        dataSource={group.reporters}
        renderItem={(report: ContentReportResponse) => (
          <List.Item
            actions={[
              report.status === 'PENDING' ? (
                <Popconfirm
                  title="Rejeter ce signalement ?"
                  onConfirm={() => dismissMutation.mutate(report.id)}
                  okText="Rejeter"
                  cancelText="Annuler"
                >
                  <Button
                    size="small"
                    icon={<CloseCircleOutlined />}
                    loading={dismissMutation.isPending}
                  >
                    Rejeter
                  </Button>
                </Popconfirm>
              ) : undefined,
            ].filter(Boolean)}
          >
            <List.Item.Meta
              title={
                <Space>
                  <Text style={{ fontSize: 13 }}>{report.reporter.email}</Text>
                  <Tag
                    style={{
                      backgroundColor: `${STATUS_COLORS[report.status] ?? neutral[300]}14`,
                      color: STATUS_COLORS[report.status] ?? neutral[500],
                      borderColor: `${STATUS_COLORS[report.status] ?? neutral[300]}40`,
                      borderRadius: 100,
                      fontSize: 11,
                    }}
                  >
                    {STATUS_LABELS[report.status] ?? report.status}
                  </Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {REASON_LABELS[report.reason] ?? report.reason}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {new Date(report.createdAt).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Drawer>
  );
}
